import {
  Avatar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuGroupHeader,
  MenuDivider,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Spinner,
  Tag,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import {
  WeatherSunny20Regular,
  WeatherMoon20Regular,
  Desktop20Regular,
  SignOut20Regular,
  ChevronDown16Regular,
  Checkmark16Filled,
  People20Regular,
  People16Regular,
  Building16Regular,
  Cart24Regular,
  Open20Regular,
} from '@fluentui/react-icons';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { logout } from '../api/auth.js';
import { useTenantSwitch } from '../lib/useTenantSwitch.js';
import { useAuthStore, usePuedeVerActividad } from '../stores/authStore.js';
import { brandingProductoActivo } from '../lib/brandingDelProducto.js';
import { usePlanoAHpVisible } from '../lib/planoGate.js';
import { useIsEcosoftStaff } from '../stores/authStore.js';
import { useUsageStore } from '../stores/usageStore.js';
import {
  approxFromRemaining,
  fallbackPlanEquivalence,
} from '../lib/planEquivalence.js';
import { useThemeStore, resolveTheme, type ThemeMode } from '../stores/themeStore.js';
import { UsagePill } from './UsagePill.js';
import { CreditosChip } from './CreditosChip.js';
import { MensajesBoton } from './mensajes/MensajesBoton.js';
import { enCreditos } from '../lib/creditos.js';
import { TiendaDialog } from './TiendaDialog.js';
import { TokenAlertBanner } from './TokenAlertBanner.js';
import { DemoGuide } from './DemoGuide.js';
import { OpusLauncherBar, OpusLauncherButton, OpusLauncherRoot } from './OpusLauncher.js';
import { controlDelHeader, controlIcono } from './controlDelHeader.js';
import { embebidoEnHub, temaDelMensaje } from '../lib/embebidoEnHub.js';
import { hubOrigin } from '../api/hub.js';
import {
  exitPublicDemo,
  publicDemoActivation,
  publicDemoProductUrl,
} from '../lib/publicDemo.js';

/** Celular: mismo corte que la barrita del launcher y que OPUS bi. */
const MOVIL = '@media (max-width: 639px)';

const useStyles = makeStyles({
  /** Topbar + barra demo en un solo bloque sticky (evita que el banner se tape al scrollear). */
  chrome: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    backgroundColor: 'var(--bg-header)',
    maxWidth: '100%',
    minWidth: 0,
  },
  topbar: {
    height: 'var(--topbar-h)',
    display: 'flex',
    alignItems: 'center',
    // padding-right extra: deja aire al aro dorado del avatar admin
    // (outline/box-shadow no cabe si el padding llega justo al borde).
    padding: '0 var(--space-xxl)',
    paddingRight: 'calc(var(--space-xxl) + 6px)',
    gap: 'var(--space-m)',
    backdropFilter: 'blur(20px) saturate(1.8)',
    backgroundColor: 'var(--bg-header)',
    borderBottom: '1px solid var(--border-subtle)',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    [MOVIL]: { padding: '0 16px', gap: '8px' },
  },
  brandCluster: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-m)',
    textDecoration: 'none',
    color: 'inherit',
  },
  logoMark: {
    width: '32px',
    height: '32px',
    borderRadius: 'var(--radius-md)',
    background: 'linear-gradient(135deg, var(--opus-brand-60), var(--opus-brand-40))',
    display: 'grid',
    placeItems: 'center',
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    fontSize: '14px',
    color: 'white',
    boxShadow: 'var(--glow-brand)',
  },
  logoText: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: 'var(--fs-400)',
    color: 'var(--text-primary)',
    whiteSpace: 'nowrap',
    [MOVIL]: { display: 'none' },
  },
  /** Lo que no cabe en celular (plan, carrito, empresa): sigue en el menú del avatar / la tienda. */
  soloEscritorio: {
    display: 'contents',
    [MOVIL]: { display: 'none' },
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-s)',
    marginLeft: 'var(--space-l)',
    flexShrink: 1,
    minWidth: 0,
    overflow: 'hidden',
    // En celular la navegación se desliza en lugar de quedar cortada.
    [MOVIL]: { marginLeft: 0, overflowX: 'auto' },
  },
  navLink: {
    color: 'var(--text-secondary)',
    textDecoration: 'none',
    fontSize: '13.5px',
    // Como las pestañas del hub: rectángulo de radio 8px del alto de los controles.
    display: 'inline-flex',
    alignItems: 'center',
    height: 'var(--control-h)',
    boxSizing: 'border-box',
    padding: '0 13px',
    borderRadius: 'var(--radius-control)',
    fontWeight: 550,
    whiteSpace: 'nowrap',
    transitionProperty: 'background, color',
    transitionDuration: '150ms',
    ':hover': {
      background: 'var(--bg-hover)',
      color: 'var(--text-primary)',
    },
  },
  navLinkActive: {
    background: 'var(--accent-weak)',
    color: 'var(--accent)',
    ':hover': {
      background: 'var(--accent-weak)',
      color: 'var(--accent)',
    },
  },
  spacer: { flex: 1 },
  // Tokens restantes del mes (+ equivalencia aproximada).
  conceptsHeadline: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 1,
    minWidth: 0,
    overflow: 'hidden',
    // En celular solo la cifra ("812 créditos"), sin el "Créditos:" / "Tokens del mes:".
    [MOVIL]: { '& > span:first-child': { display: 'none' } },
  },
  topTrailing: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-s)',
    flexShrink: 1,
    minWidth: 0,
    // visible: el aro del avatar admin no debe recortarse
    overflow: 'visible',
    // En celular lo que cede espacio es la navegación (se desliza), no el avatar.
    [MOVIL]: { flexShrink: 0, gap: '4px' },
  },
  conceptsSep: {
    color: 'var(--text-tertiary)',
    fontWeight: 500,
  },
  // El indicador de tokens es un BOTÓN: abre el desglose de lo que queda.
  tokensBtn: {
    ...controlDelHeader,
    flexShrink: 1,
    color: 'var(--text-secondary)',
  },
  tokensPop: {
    minWidth: '300px',
    maxWidth: '360px',
    padding: 'var(--space-l) var(--space-xl)',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  tokensPopTitle: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: 'var(--fs-300)',
  },
  tokensPopBig: {
    fontSize: 'var(--fs-500)',
    fontWeight: 700,
    color: 'var(--text-primary)',
    fontVariantNumeric: 'tabular-nums',
  },
  tokensPopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
  },
  tokensPopVal: {
    fontWeight: 600,
    color: 'var(--text-primary)',
    fontVariantNumeric: 'tabular-nums',
  },
  tokensPopLabel: {
    marginTop: 'var(--space-s)',
    fontSize: 'var(--fs-100)',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
  },
  tokensPopEq: {
    margin: 0,
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
    lineHeight: 1.5,
  },
  tokensPopNote: {
    margin: 0,
    marginTop: '4px',
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    lineHeight: 1.4,
  },
  /** Carrito y tema: cuadrados de 38px con borde fino, como el botón de tema del hub. */
  iconBtn: controlIcono,
  tenantPill: {
    ...controlDelHeader,
    gap: '6px',
    padding: '0 10px 0 12px',
    fontWeight: 500,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '180px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  tenantPillText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  /** El equipo se distingue de la empresa a simple vista, no solo por el nombre. */
  tenantPillTeam: {
    background: 'var(--accent-weak)',
    border: '1px solid var(--accent)',
    color: 'var(--accent)',
  },
  tenantMenuHint: {
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    padding: '2px 12px 8px',
    maxWidth: '300px',
    lineHeight: 1.35,
  },
  avatarBtn: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    borderRadius: '50%',
    lineHeight: 0,
    flexShrink: 0,
    // Espacio para el aro dorado sin que el flex lo empuje fuera del viewport.
    margin: '3px',
  },
  // box-shadow en vez de outline: outline se pintaba fuera y el topbar lo cortaba.
  avatarStaff: {
    boxShadow: '0 0 0 2px var(--bg-header), 0 0 0 4px var(--opus-accent-60)',
  },
  menuHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px 12px',
  },
  menuUser: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  menuName: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: 'var(--fs-300)',
    color: 'var(--text-primary)',
    lineHeight: 1.2,
  },
  menuEmail: {
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    fontFamily: 'var(--font-mono)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  menuTags: {
    display: 'flex',
    gap: '4px',
    marginTop: '6px',
  },
  /** Aclaración bajo el nombre en el sitio de demostración. */
  menuNote: {
    fontSize: 'var(--fs-100)',
    color: 'var(--text-secondary)',
    lineHeight: 1.35,
  },
  menuSection: {
    padding: '8px 16px 10px',
  },
  menuLabel: {
    fontSize: '10px',
    color: 'var(--text-tertiary)',
    fontFamily: 'var(--font-mono)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: '6px',
  },
  menuValue: {
    fontSize: 'var(--fs-200)',
    color: 'var(--text-primary)',
  },
  themeRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '2px',
    background: 'var(--bg-raised)',
    borderRadius: '8px',
    padding: '3px',
  },
  themeBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '3px',
    padding: '6px 4px',
    border: 'none',
    background: 'transparent',
    borderRadius: '6px',
    cursor: 'pointer',
    color: 'var(--text-tertiary)',
    fontSize: '10px',
    fontFamily: 'var(--font-mono)',
    transitionProperty: 'color, background',
    transitionDuration: '120ms',
    ':hover': {
      color: 'var(--text-primary)',
    },
  },
  themeBtnActive: {
    background: 'var(--bg-overlay)',
    color: 'var(--text-primary)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.20)',
  },
});

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Miembro',
  viewer: 'Viewer',
};

const THEME_OPTIONS: { value: ThemeMode; icon: ReactNode; label: string }[] = [
  { value: 'system', icon: <Desktop20Regular />, label: 'Auto' },
  { value: 'light', icon: <WeatherSunny20Regular />, label: 'Claro' },
  { value: 'dark', icon: <WeatherMoon20Regular />, label: 'Oscuro' },
];

const fmtN = new Intl.NumberFormat('es-MX');

/**
 * Tokens restantes del mes (bolsa + kits en el total de eko27). Es un botón:
 * al hacer clic abre el desglose de lo que queda y a qué alcanza — informativo,
 * separado del popover de la cuenta (UsagePill), que sigue igual.
 */
function TokensHeadline({ className }: { className: string }) {
  const styles = useStyles();
  const usage = useUsageStore((s) => s.data);
  const [open, setOpen] = useState(false);
  if (!usage?.tokens) return null;

  const { remaining, quota, used } = usage.tokens;
  if (!quota) return null;
  const pctUsed = Math.round((used / quota) * 100);
  const color =
    pctUsed >= 90 ? 'var(--bad)' :
    pctUsed >= 75 ? 'var(--warn)' :
    'var(--text-primary)';

  const packTokens = usage.tokens.pack_tokens ?? 0;
  const bolsaRestante = Math.max(0, quota - used);
  const eqRaw = usage.plan_equivalence
    ?? fallbackPlanEquivalence(usage.plan, usage.seats ?? 1);
  const eqTokens = eqRaw.tokens_month || eqRaw.tokens_week || quota || 1;
  // Cada renglón es una ALTERNATIVA sobre el mismo saldo (o una cosa o la
  // otra), calculada con el total restante, recargas incluidas.
  const approx = approxFromRemaining(remaining, {
    concepts: eqRaw.concepts,
    apus: eqRaw.apus,
    chats: eqRaw.chats,
    tokens_month: eqTokens,
  });

  return (
    <Popover withArrow positioning="below" open={open} onOpenChange={(_, d) => setOpen(d.open)}>
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={mergeClasses(className, styles.tokensBtn)}
          aria-label="Ver qué puedes hacer con tus tokens restantes"
          title="¿Qué me alcanza con estos tokens?"
        >
          <span>Tokens del mes:</span>
          <strong style={{ color }} title={`${fmtN.format(used)} de ${fmtN.format(quota)}`}>
            {fmtN.format(remaining)} restantes
          </strong>
        </button>
      </PopoverTrigger>
      <PopoverSurface className={styles.tokensPop}>
        <span className={styles.tokensPopTitle}>Lo que te queda</span>
        <span className={styles.tokensPopBig}>{fmtN.format(remaining)} tokens</span>
        <div className={styles.tokensPopRow}>
          <span>Bolsa mensual</span>
          <span className={styles.tokensPopVal}>
            {fmtN.format(bolsaRestante)} de {fmtN.format(quota)}
          </span>
        </div>
        <div className={styles.tokensPopRow}>
          <span>Recargas</span>
          <span className={styles.tokensPopVal}>{fmtN.format(packTokens)}</span>
        </div>

        <span className={styles.tokensPopLabel}>Te alcanza para</span>
        <p className={styles.tokensPopEq}>
          ≈ <strong>{fmtN.format(approx.concepts)}</strong> conceptos cotizados
          <br />o <strong>{fmtN.format(approx.apus)}</strong> APUs generados con IA
          <br />o <strong>{fmtN.format(approx.chats)}</strong> chats
        </p>
        <p className={styles.tokensPopNote}>
          Es una u otra cosa sobre el mismo saldo, no las tres juntas. Cada
          operación descuenta según su tamaño; usa estas cifras como guía.
        </p>
      </PopoverSurface>
    </Popover>
  );
}

/** Con CREDITOS_ENABLED muestra "N créditos" con desglose; apagado, el indicador de tokens de siempre. */
function CreditosOTokens({ className, onBuy }: { className: string; onBuy: () => void }) {
  const usage = useUsageStore((s) => s.data);
  if (enCreditos(usage)) return <CreditosChip className={className} onBuy={onBuy} />;
  return <TokensHeadline className={className} />;
}

export function AppTopBar() {
  const usageData = useUsageStore((s) => s.data);
  const styles = useStyles();
  const navigate = useNavigate();
  const {
    user,
    tenant,
    availableTenants,
    isAdmin,
    canAdminEcostos,
    clearSession,
    demoGuest,
    demoProductUrl,
  } = useAuthStore(
    (s) => ({
      user: s.user,
      tenant: s.tenant,
      availableTenants: s.availableTenants,
      // Política 2026-05-12: borde dorado = admin de la empresa actual.
      isAdmin: s.tenant?.role === 'admin' || s.tenant?.role === 'owner',
      // Nav "Admin" (ECOSTOS sync) solo si admin + empresa ECOSOFT.
      canAdminEcostos:
        !!s.tenant?.isEcosoftOwned &&
        (s.tenant?.role === 'admin' || s.tenant?.role === 'owner'),
      clearSession: s.clearSession,
      demoGuest: s.demoGuest,
      demoProductUrl: s.demoProductUrl,
    }),
  );
  // Lo decide el servidor: la lista de correos vive solo en el API.
  const puedeVerActividad = usePuedeVerActividad();
  // Marca del producto activo (ai/ci) para el logo del topbar.
  const marca = brandingProductoActivo();
  // /planos (WIP, staff-only): flag del front + staff Ecosoft.
  const planoAHpVisible = usePlanoAHpVisible();
  // Demos de funciones futuras: solo en OPUS ci y solo staff Ecosoft.
  const demosVisibles = useIsEcosoftStaff() && marca.producto === 'ci';
  const themeMode = useThemeStore((s) => s.mode);
  const setThemeMode = useThemeStore((s) => s.setMode);
  const [tiendaOpen, setTiendaOpen] = useState(false);
  const { switchingSlug, switchTo } = useTenantSwitch();
  // Sin sesión del hub (en local `ci.localhost` no recibe la cookie del hub) la
  // barrita muestra a quien ya está dentro de esta app.
  const launcherUser = useMemo(
    () => (user ? { name: user.name, email: user.email } : null),
    [user],
  );
  const enHub = embebidoEnHub();
  // Embebida: sigue el tema del hub (solo mensajes del origen del hub).
  useEffect(() => {
    if (!enHub) return;
    const origen = hubOrigin();
    const onMsg = (ev: MessageEvent) => {
      if (!origen || ev.origin !== origen) return;
      const tema = temaDelMensaje(ev.data);
      if (tema) setThemeMode(tema);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [enHub, setThemeMode]);

  if (!user || !tenant) return null;

  async function onLogout() {
    await logout();
    clearSession();
    navigate('/login', { replace: true });
  }

  /**
   * Salir de la demostración: además de cerrar la sesión de visitante, apaga el
   * modo demostración de la pestaña y recarga, para no quedar a medio camino.
   */
  async function onExitDemo() {
    exitPublicDemo();
    await logout().catch(() => undefined);
    clearSession();
    window.location.replace('/login');
  }

  // Sitio de demostración: quien lo visita no entró con una cuenta, así que no
  // hay nada que cerrar. En lugar de eso se le ofrece pasar a OPUS ai.
  const productUrl = demoProductUrl || publicDemoProductUrl();
  // …salvo que esto NO sea el sitio de demostración: si alguien quedó como
  // visitante en otra dirección (la demostración se prendió a mano con
  // `?demo=1`, o sobró la sesión de visitante de una prueba anterior), ahí sí
  // hay a dónde volver y hay que ofrecer la puerta.
  const demoConSalida = demoGuest && publicDemoActivation() !== 'host';

  const userInitials = initials(user.name);
  const roleLabel = ROLE_LABEL[tenant.role] ?? tenant.role;

  // Contextos disponibles: la empresa de la licencia ("Mi plan") y los equipos
  // de eko27, que en OPUS ai son empresas propias. El servidor ya los manda en
  // ese orden; aquí solo se separan para poder titular cada bloque.
  const empresas = availableTenants.filter((t) => t.kind !== 'team');
  const equipos = availableTenants.filter((t) => t.kind === 'team');
  const puedeCambiar = availableTenants.length > 1;
  const enEquipo = tenant.kind === 'team';

  function renderContexto(t: { slug: string; name: string; kind?: 'team' | 'company' }) {
    const activo = t.slug === tenant!.slug;
    const cambiando = switchingSlug === t.slug;
    return (
      <MenuItem
        key={t.slug}
        persistOnClick
        disabled={activo || switchingSlug !== null}
        icon={
          cambiando ? <Spinner size="extra-tiny" />
            : activo ? <Checkmark16Filled />
              : t.kind === 'team' ? <People16Regular /> : <Building16Regular />
        }
        onClick={() => {
          if (activo) return;
          void switchTo({ slug: t.slug, name: t.name, kind: t.kind });
        }}
      >
        {t.name}
      </MenuItem>
    );
  }

  // La barra OPUS es la misma dentro del hub y en el sitio de la app (en el hub habla con él).
  const hideLauncher = demoGuest || import.meta.env.VITE_DEMO === '1';

  return (
    <OpusLauncherRoot currentApp={marca.producto} hidden={hideLauncher} enHub={enHub} appUser={launcherUser}>
    <div className={styles.chrome}>
    <OpusLauncherBar />
    <header className={styles.topbar}>
      <div className={styles.brandCluster}>
        <OpusLauncherButton />
        <Link to="/projects" className={styles.brand}>
          <div
            className={styles.logoMark}
            // Logo = favicon = tile de la barrita: misma fuente (brandingDelProducto) para
            // ai (azul #0c6fd6) y ci (magenta #D6299B).
            style={{ background: `linear-gradient(135deg, ${marca.c1}, ${marca.c2})` }}
          >
            {marca.letras}
          </div>
          <div className={styles.logoText}>{marca.nombre}{demoGuest ? ' · demo' : ''}</div>
        </Link>
      </div>

      <nav className={styles.nav}>
        <NavLink
          to="/projects"
          className={({ isActive }) => mergeClasses(styles.navLink, isActive && styles.navLinkActive)}
        >
          Proyectos
        </NavLink>
        <NavLink
          to="/catalogs"
          className={({ isActive }) => mergeClasses(styles.navLink, isActive && styles.navLinkActive)}
        >
          Catálogos de referencia
        </NavLink>
        {!demoGuest && (
          <>
            <NavLink
              to="/files"
              className={({ isActive }) => mergeClasses(styles.navLink, isActive && styles.navLinkActive)}
            >
              Mis archivos
            </NavLink>
            {demosVisibles && (
              <NavLink
                to="/demos"
                className={({ isActive }) => mergeClasses(styles.navLink, isActive && styles.navLinkActive)}
              >
                Demos
              </NavLink>
            )}
            <NavLink
              to="/settings/members"
              className={({ isActive }) => mergeClasses(styles.navLink, isActive && styles.navLinkActive)}
            >
              Admin
            </NavLink>
          </>
        )}
      </nav>

      <div className={styles.spacer} />

      {/* Lado derecho, MISMO orden que OPUS bi: créditos · plan · (carrito) · tema ·
          (empresa) · avatar. */}
      <div className={styles.topTrailing}>
      {!demoGuest && (
        <CreditosOTokens className={styles.conceptsHeadline} onBuy={() => setTiendaOpen(true)} />
      )}
      {/* Mensajes (docs/29): junto a créditos; no se pinta si el hub aún no lo tiene. */}
      {!demoGuest && <MensajesBoton enHub={enHub} />}

      {/* Acá vivía el indicador de avance de la cotización. Se retiró el
          2026-08-23: al hacerle clic llevaba a la pantalla vieja de análisis de
          precios, que ya no se usa. El avance se ve entrando al presupuesto. */}
      <div className={styles.soloEscritorio}>
      {!demoGuest && <UsagePill />}

      {!demoGuest && (
      <button
        type="button"
        className={styles.iconBtn}
        onClick={() => setTiendaOpen(true)}
        aria-label={enCreditos(usageData) ? 'Comprar créditos' : 'Comprar tokens'}
        title={enCreditos(usageData) ? 'Comprar créditos' : 'Comprar tokens'}
      >
        <Cart24Regular />
      </button>
      )}
      </div>

      <button
        type="button"
        className={styles.iconBtn}
        onClick={() => setThemeMode(resolveTheme(themeMode) === 'dark' ? 'light' : 'dark')}
        aria-label="Cambiar tema"
        title="Cambiar tema (claro / oscuro)"
      >
        {resolveTheme(themeMode) === 'dark' ? <WeatherSunny20Regular /> : <WeatherMoon20Regular />}
      </button>

      {/* Contexto activo. Con más de un contexto es un menú: "Mi plan" (la
          empresa de la licencia) y los equipos a los que pertenece. El cambio
          es en vivo — `switch-tenant` revalida el derecho contra la empresa
          destino y niega el cambio si no puede afirmarlo. */}
      <div className={styles.soloEscritorio}>
      {demoGuest || !puedeCambiar ? (
        <span
          className={mergeClasses(styles.tenantPill, !demoGuest && enEquipo && styles.tenantPillTeam)}
          aria-label={demoGuest ? 'Sitio de demostración' : 'Empresa activa'}
        >
          {!demoGuest && enEquipo && <People16Regular />}
          <span className={styles.tenantPillText}>{demoGuest ? 'Demostración' : tenant.name}</span>
        </span>
      ) : (
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <button
              type="button"
              className={mergeClasses(styles.tenantPill, enEquipo && styles.tenantPillTeam)}
              aria-label={`Contexto activo: ${tenant.name}. Cambiar de empresa o equipo`}
              title={enEquipo ? `Equipo ${tenant.name} — cambiar de contexto` : `${tenant.name} — cambiar de contexto`}
            >
              {enEquipo && <People16Regular />}
              <span className={styles.tenantPillText}>{tenant.name}</span>
              <ChevronDown16Regular />
            </button>
          </MenuTrigger>
          <MenuPopover style={{ minWidth: 260 }}>
            <MenuList>
              {empresas.length > 0 && <MenuGroupHeader>Mi plan</MenuGroupHeader>}
              {empresas.map(renderContexto)}
              {equipos.length > 0 && empresas.length > 0 && <MenuDivider />}
              {equipos.length > 0 && <MenuGroupHeader>Equipos</MenuGroupHeader>}
              {equipos.map(renderContexto)}
            </MenuList>
            <div className={styles.tenantMenuHint}>
              Cada contexto tiene sus propios proyectos, archivos y bolsa de {enCreditos(usageData) ? 'créditos' : 'tokens'}.
            </div>
          </MenuPopover>
        </Menu>
      )}
      </div>

      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <button
            type="button"
            className={mergeClasses(styles.avatarBtn, isAdmin && styles.avatarStaff)}
            aria-label={`Menú de ${user.name}`}
          >
            <Avatar name={user.name} initials={userInitials} size={32} color="brand" />
          </button>
        </MenuTrigger>
        <MenuPopover style={{ minWidth: 280 }}>
          <div className={styles.menuHeader}>
            <Avatar name={user.name} initials={userInitials} size={40} color="brand" />
            <div className={styles.menuUser}>
              <span className={styles.menuName}>{demoGuest ? 'Visitante' : user.name}</span>
              {demoGuest ? (
                <span className={styles.menuNote}>Estás recorriendo una demostración</span>
              ) : (
                <>
                  <span className={styles.menuEmail} title={user.email}>{user.email}</span>
                  <div className={styles.menuTags}>
                    <Tag size="extra-small">{roleLabel}</Tag>
                  </div>
                </>
              )}
            </div>
          </div>

          <MenuDivider />

          {!demoGuest && (
            <>
              <div className={styles.menuSection}>
                <div className={styles.menuLabel}>{enEquipo ? 'Equipo activo' : 'Empresa activa'}</div>
                <div className={styles.menuValue}>{tenant.name}</div>
              </div>

              <MenuDivider />
            </>
          )}

          <div className={styles.menuSection}>
            <div className={styles.menuLabel}>Tema</div>
            <div className={styles.themeRow} role="radiogroup" aria-label="Tema">
              {THEME_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={themeMode === opt.value}
                  className={mergeClasses(
                    styles.themeBtn,
                    themeMode === opt.value && styles.themeBtnActive,
                  )}
                  onClick={() => setThemeMode(opt.value)}
                  title={opt.label}
                >
                  {opt.icon}
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          <MenuDivider />

          <MenuList>
            {!demoGuest && (
              <MenuItem
                icon={<People20Regular />}
                onClick={() => navigate('/settings/members')}
              >
                Administración
              </MenuItem>
            )}
            {canAdminEcostos && (
              <MenuItem onClick={() => navigate('/staff/usage')}>
                Consumo por tenant (staff)
              </MenuItem>
            )}
            {canAdminEcostos && (
              <MenuItem onClick={() => navigate('/staff/adlic-sim')}>
                Simulación ADLIC (staff)
              </MenuItem>
            )}
            {canAdminEcostos && (
              <MenuItem onClick={() => navigate('/staff/apu-library')}>
                Biblioteca de análisis con IA (staff)
              </MenuItem>
            )}
            {canAdminEcostos && (
              <MenuItem onClick={() => navigate('/staff/calibracion')}>
                Calibración del motor (staff)
              </MenuItem>
            )}
            {canAdminEcostos && (
              <MenuItem onClick={() => navigate('/staff/precios')}>
                Auditoría de precios (staff)
              </MenuItem>
            )}
            {canAdminEcostos && (
              <MenuItem onClick={() => navigate('/staff/context-help')}>
                Ayuda contextual (staff)
              </MenuItem>
            )}
            {canAdminEcostos && (
              <MenuItem onClick={() => navigate('/staff/sesiones-activas')}>
                Sesiones activas (staff)
              </MenuItem>
            )}
            {canAdminEcostos && (
              <MenuItem onClick={() => navigate('/staff/importaciones')}>
                Importaciones a OPUS 27 (staff)
              </MenuItem>
            )}
            {/* WIP oculto: flag del front prendido + staff Ecosoft (usePlanoAHpVisible). */}
            {planoAHpVisible && (
              <MenuItem onClick={() => navigate('/planos')}>
                Lectura de planos (WIP)
              </MenuItem>
            )}
            {puedeVerActividad && (
              <MenuItem onClick={() => navigate('/staff/actividad')}>
                Actividad de usuarios (staff)
              </MenuItem>
            )}
            {demosVisibles && (
              <MenuItem onClick={() => navigate('/staff/actividad-ci')}>
                Actividad de OPUS ci (staff)
              </MenuItem>
            )}
            {demoGuest ? (
              <>
                <MenuItem
                  icon={<Open20Regular />}
                  onClick={() => window.open(productUrl, '_blank', 'noopener,noreferrer')}
                >
                  Entrar a OPUS ai con mi cuenta
                </MenuItem>
                {demoConSalida && (
                  <MenuItem icon={<SignOut20Regular />} onClick={() => void onExitDemo()}>
                    Salir de la demostración
                  </MenuItem>
                )}
              </>
            ) : (
              <MenuItem icon={<SignOut20Regular />} onClick={() => void onLogout()}>
                Cerrar sesión
              </MenuItem>
            )}
          </MenuList>
        </MenuPopover>
      </Menu>
      </div>

      <TiendaDialog open={tiendaOpen} onClose={() => setTiendaOpen(false)} />
    </header>
    {!demoGuest && import.meta.env.VITE_DEMO !== '1' && <TokenAlertBanner />}
    {demoGuest && <DemoGuide embedded />}
    </div>
    </OpusLauncherRoot>
  );
}
